import type { AgentApproval } from 'Resources/AgentApprovals';
import { executionFailureMessage, parseApprovalPayload } from './approvalAdapters';
import type { ToolProposal, ToolProposalField } from './types';

/** The approval actions shown on the generic ToolProposalCard. */
export const TOOL_PROPOSAL_ACTIONS = new Set([
	'sendUserEmail',
	'sendSlackMessage',
	'connectionInvoke',
	'manageAutomations',
	'manageAgents',
	'delegateTask'
]);

/** Where the backend keeps the card's lines and capability on the approval payload. */
const PROPOSAL_FIELDS_KEY = '_proposalFields';
const PROPOSAL_CAPABILITY_KEY = '_proposalCapability';

const toFields = (raw: unknown): ToolProposalField[] =>
	Array.isArray(raw)
		? raw
				.filter((f): f is { key: unknown; value: unknown } => !!f && typeof f === 'object')
				.map(f => ({ key: String(f.key ?? ''), value: String(f.value ?? '') }))
				.filter(f => f.key)
		: [];

/** Rebuild a ToolProposal from its `agent_approval_request` row after a reload. */
export const approvalToToolProposal = (a: AgentApproval): ToolProposal => {
	const payload = parseApprovalPayload(a.actionPayload) ?? {};
	const capability = payload[PROPOSAL_CAPABILITY_KEY];
	const failure = executionFailureMessage(a.resultPayload as Record<string, unknown> | null | undefined);
	let status: ToolProposal['status'];
	if (failure !== null) {
		status = 'error';
	} else {
		switch (a.status) {
			case 'approved':
				status = 'saved';
				break;
			case 'rejected':
			case 'changes_requested':
			case 'expired':
				status = 'cancelled';
				break;
			default:
				status = 'pending';
		}
	}
	return {
		tool: a.action,
		capability: typeof capability === 'string' ? capability : null,
		fields: toFields(payload[PROPOSAL_FIELDS_KEY]),
		status,
		error: failure || undefined,
		approvalId: a.id
	};
};
